import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Container,
  LinearProgress,
  TextField,
  InputAdornment,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  ViewWeek as ViewWeekIcon,
  CalendarMonth as CalendarMonthIcon,
  ViewDay as ViewDayIcon,
  Today as TodayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Groups as GroupsIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  FolderOpen as LoadIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, isSameDay, isToday, parseISO } from 'date-fns';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';

// Import components
import ScheduleCalendar from './components/ScheduleCalendar';
import ShiftEditor from './components/ShiftEditor';
import ShiftViewDialog from './components/ShiftViewDialog';
import DepartmentFilter from './components/DepartmentFilter';
import EmployeeList from './components/EmployeeList';
import ScheduleStats from './components/ScheduleStats';

const ScheduleContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

const Schedule = () => {
  const theme = useTheme();
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, canEditPage, canDeletePage } = usePermissions();
  
  // Determine active tab based on URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/tasks')) return 1;
    if (path.includes('/analytics')) return 2;
    return 0;
  };
  
  // State management
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // day, week
  const [selectedTab, setSelectedTab] = useState(getActiveTab());
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskEditorOpen, setTaskEditorOpen] = useState(false);
  const [taskViewOpen, setTaskViewOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeFormat, setTimeFormat] = useState('24hour');
  const [saveDayTemplateOpen, setSaveDayTemplateOpen] = useState(false);
  const [loadDayTemplateOpen, setLoadDayTemplateOpen] = useState(false);
  const [dayTemplates, setDayTemplates] = useState([]);
  const [weekTemplates, setWeekTemplates] = useState([]);
  const [clearCalendarOpen, setClearCalendarOpen] = useState(false);
  const [clearStartDate, setClearStartDate] = useState('');
  const [clearEndDate, setClearEndDate] = useState('');
  const [loadStartDate, setLoadStartDate] = useState('');
  const [loadEndDate, setLoadEndDate] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [saveWeekTemplateOpen, setSaveWeekTemplateOpen] = useState(false);
  const [loadWeekTemplateOpen, setLoadWeekTemplateOpen] = useState(false);
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Update tab when location changes
  useEffect(() => {
    setSelectedTab(getActiveTab());
  }, [location.pathname]);

  // Fetch user preferences
  useEffect(() => {
    if (companyId) {
      fetchUserPreferences();
    }
  }, [companyId]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchEmployees(),
          fetchDepartments(),
          fetchTasks(),
          fetchDayTemplates(),
          fetchWeekTemplates()
        ]);
      } catch (error) {
        console.error('Error fetching task data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          department:departments(*)
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('first_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);

      const { data, error } = await supabase
        .from('employee_shifts')
        .select(`
          *,
          employee:employees(*),
          department:departments(*)
        `)
        .eq('company_id', companyId)
        .gte('shift_date', format(weekStart, 'yyyy-MM-dd'))
        .lte('shift_date', format(weekEnd, 'yyyy-MM-dd'))
        .order('shift_date')
        .order('start_time');

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: preferences, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (preferences) {
        setTimeFormat(preferences.time_format || '12hour');
      } else {
        await createUserPreferences(user.id);
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
    }
  };

  const createUserPreferences = async (userId) => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert([{
          user_id: userId,
          time_format: '12hour'
        }], {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setTimeFormat('12hour');
    } catch (err) {
      console.error('Error creating user preferences:', err);
    }
  };

  const updateTimeFormat = async (newFormat) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          time_format: newFormat
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setTimeFormat(newFormat);
    } catch (err) {
      console.error('Error updating time format:', err);
    }
  };

  const fetchDayTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('shift_templates')
        .select('*')
        .eq('company_id', companyId)
        .eq('template_type', 'day')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDayTemplates(data || []);
    } catch (error) {
      console.error('Error fetching day templates:', error);
    }
  };

  const fetchWeekTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('shift_templates')
        .select('*')
        .eq('company_id', companyId)
        .eq('template_type', 'week')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWeekTemplates(data || []);
    } catch (error) {
      console.error('Error fetching week templates:', error);
    }
  };

  const handleSaveDayTemplate = async (templateName, description) => {
    try {
      const currentDateStr = format(currentDate, 'yyyy-MM-dd');
      
      // Get all tasks for the current day
      const dayTasks = tasks.filter(task => task.shift_date === currentDateStr);
      
      if (dayTasks.length === 0) {
        alert('No tasks found for this day to save as template');
        return;
      }

      const taskData = dayTasks.map(task => ({
        employee_id: task.employee_id,
        department_id: task.department_id,
        start_time: task.start_time,
        end_time: task.end_time,
        job_position: task.job_position,
        role_documentation: task.role_documentation,
        notes: task.notes
      }));

      const { error } = await supabase
        .from('shift_templates')
        .insert({
          company_id: companyId,
          name: templateName,
          description: description,
          template_type: 'day',
          template_data: { tasks: taskData }
        });

      if (error) throw error;
      await fetchDayTemplates();
      setSaveDayTemplateOpen(false);
    } catch (error) {
      console.error('Error saving day template:', error);
    }
  };

  const handleSaveWeekTemplate = async (templateName, description) => {
    try {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      
      // Get all tasks for the current week
      const weekTasks = tasks.filter(task => {
        const taskDate = parseISO(task.shift_date);
        return taskDate >= weekStart && taskDate <= weekEnd;
      });
      
      if (weekTasks.length === 0) {
        alert('No tasks found for this week to save as template');
        return;
      }

      const taskData = weekTasks.map(task => ({
        employee_id: task.employee_id,
        department_id: task.department_id,
        shift_date: task.shift_date,
        start_time: task.start_time,
        end_time: task.end_time,
        job_position: task.job_position,
        role_documentation: task.role_documentation,
        notes: task.notes
      }));

      const { error } = await supabase
        .from('shift_templates')
        .insert({
          company_id: companyId,
          name: templateName,
          description: description,
          template_type: 'week',
          template_data: { 
            tasks: taskData,
            date_range: {
              start_date: format(weekStart, 'yyyy-MM-dd'),
              end_date: format(weekEnd, 'yyyy-MM-dd')
            }
          }
        });

      if (error) throw error;
      await fetchWeekTemplates();
      setSaveWeekTemplateOpen(false);
    } catch (error) {
      console.error('Error saving week template:', error);
    }
  };

  const handleOpenLoadTemplate = () => {
    if (viewMode === 'day') {
      const currentDateStr = format(currentDate, 'yyyy-MM-dd');
      setLoadStartDate(currentDateStr);
      setLoadEndDate(currentDateStr);
      setSelectedTemplateId(null);
      setLoadDayTemplateOpen(true);
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      setLoadStartDate(format(weekStart, 'yyyy-MM-dd'));
      setLoadEndDate(format(weekEnd, 'yyyy-MM-dd'));
      setSelectedTemplateId(null);
      setLoadWeekTemplateOpen(true);
    }
  };

  const handleLoadDayTemplate = async () => {
    try {
      if (!selectedTemplateId) {
        alert('Please select a template');
        return;
      }

      const template = dayTemplates.find(t => t.id === selectedTemplateId);
      if (!template || !template.template_data?.tasks) {
        alert('Template not found or has no task data');
        return;
      }

      // Calculate date range
      const startDate = dayjs(loadStartDate);
      const endDate = dayjs(loadEndDate);
      const daysDiff = endDate.diff(startDate, 'day');
      
      const allTasksToCreate = [];

      // Create tasks for each day in the range
      for (let i = 0; i <= daysDiff; i++) {
        const targetDate = startDate.add(i, 'day');
        const targetDateStr = targetDate.format('YYYY-MM-DD');
        
        const tasksForDay = template.template_data.tasks.map(taskData => ({
          ...taskData,
          company_id: companyId,
          shift_date: targetDateStr
        }));
        
        allTasksToCreate.push(...tasksForDay);
      }

      const { error } = await supabase
        .from('employee_shifts')
        .insert(allTasksToCreate);

      if (error) throw error;
      await fetchTasks();
      setLoadDayTemplateOpen(false);
    } catch (error) {
      console.error('Error loading day template:', error);
    }
  };

  const handleLoadWeekTemplate = async () => {
    try {
      if (!selectedTemplateId) {
        alert('Please select a template');
        return;
      }

      const template = weekTemplates.find(t => t.id === selectedTemplateId);
      if (!template || !template.template_data?.tasks) {
        alert('Template not found or has no task data');
        return;
      }

      // Calculate date range
      const startDate = dayjs(loadStartDate);
      const endDate = dayjs(loadEndDate);
      
      const allTasksToCreate = template.template_data.tasks.map(taskData => {
        // Calculate the offset from the original template date to the target date
        const originalTaskDate = dayjs(taskData.shift_date);
        const templateStartDate = dayjs(template.template_data.date_range.start_date);
        const dayOffset = originalTaskDate.diff(templateStartDate, 'day');
        
        // Apply the same offset to the new start date
        const newTaskDate = startDate.add(dayOffset, 'day');
        
        return {
          ...taskData,
          company_id: companyId,
          shift_date: newTaskDate.format('YYYY-MM-DD')
        };
      });

      const { error } = await supabase
        .from('employee_shifts')
        .insert(allTasksToCreate);

      if (error) throw error;
      await fetchTasks();
      setLoadWeekTemplateOpen(false);
    } catch (error) {
      console.error('Error loading week template:', error);
    }
  };

  const handleDeleteTemplate = (template, event) => {
    event.stopPropagation();
    setTemplateToDelete(template);
    setDeleteTemplateOpen(true);
  };

  const handleConfirmDeleteTemplate = async () => {
    try {
      if (!templateToDelete) return;

      const { error } = await supabase
        .from('shift_templates')
        .delete()
        .eq('id', templateToDelete.id);

      if (error) throw error;

      // Refresh the appropriate template list
      if (templateToDelete.template_type === 'day') {
        await fetchDayTemplates();
      } else {
        await fetchWeekTemplates();
      }

      setDeleteTemplateOpen(false);
      setTemplateToDelete(null);
      
      // Clear selection if the deleted template was selected
      if (selectedTemplateId === templateToDelete.id) {
        setSelectedTemplateId(null);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleClearCalendar = () => {
    if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      setClearStartDate(format(weekStart, 'yyyy-MM-dd'));
      setClearEndDate(format(weekEnd, 'yyyy-MM-dd'));
    } else {
      const currentDateStr = format(currentDate, 'yyyy-MM-dd');
      setClearStartDate(currentDateStr);
      setClearEndDate(currentDateStr);
    }
    setClearCalendarOpen(true);
  };

  const handleConfirmClear = async () => {
    try {
      const { error } = await supabase
        .from('employee_shifts')
        .delete()
        .eq('company_id', companyId)
        .gte('shift_date', clearStartDate)
        .lte('shift_date', clearEndDate);
      
      if (error) throw error;
      await fetchTasks();
      setClearCalendarOpen(false);
    } catch (error) {
      console.error('Error clearing calendar:', error);
    }
  };

  // Navigation handlers
  const navigateDate = (direction) => {
    let newDate;
    if (viewMode === 'day') {
      newDate = direction === 'next' 
        ? addDays(currentDate, 1) 
        : addDays(currentDate, -1);
    } else if (viewMode === 'week') {
      newDate = direction === 'next' 
        ? addWeeks(currentDate, 1) 
        : subWeeks(currentDate, 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Task management
  const handleCreateTask = (date, employee = null) => {
    setSelectedDate(date);
    setSelectedEmployee(employee);
    setSelectedTask(null);
    setTaskEditorOpen(true);
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setTaskViewOpen(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setSelectedDate(new Date(task.shift_date));
    setSelectedEmployee(task.employee);
    setTaskViewOpen(false);
    setTaskEditorOpen(true);
  };

  const handleTaskSave = async (taskData) => {
    try {
      if (selectedTask) {
        // Update existing task
        const { error } = await supabase
          .from('employee_shifts')
          .update(taskData)
          .eq('id', selectedTask.id);
        
        if (error) throw error;
      } else {
        // Create new task
        const { error } = await supabase
          .from('employee_shifts')
          .insert({
            ...taskData,
            company_id: companyId
          });
        
        if (error) throw error;
      }
      
      await fetchTasks();
      setTaskEditorOpen(false);
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleTaskDelete = async (taskId) => {
    try {
      const { error } = await supabase
        .from('employee_shifts')
        .delete()
        .eq('id', taskId);
      
      if (error) throw error;
      await fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  // Filter employees by selected departments
  const filteredEmployees = selectedDepartments.length > 0
    ? employees.filter(emp => 
        emp.department && selectedDepartments.includes(emp.department.id)
      )
    : employees;

  // Filter tasks by selected departments (only show tasks for filtered employees)
  const filteredTasks = selectedDepartments.length > 0
    ? tasks.filter(task => {
        const employee = employees.find(emp => emp.id === task.employee_id);
        return employee && employee.department && selectedDepartments.includes(employee.department.id);
      })
    : tasks;

  const getViewTitle = () => {
    if (viewMode === 'day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  };

  // Permission check
  if (!hasPermission('schedule.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Task Management Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Task Management. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#673AB7', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#673AB7', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#673AB7', 0.1),
                mb: 2
              }}>
                <ScheduleIcon sx={{ fontSize: 48, color: '#673AB7' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#673AB7',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Task Management
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Loading task data and employee assignments...
            </Typography>
            <Box sx={{
              width: '60%',
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha('#673AB7', 0.15),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#673AB7',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container 
        maxWidth="xl" 
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >

      {/* Content Area */}
      <Box sx={{ width: '100%', mt: 4 }}>
        {/* Schedule Tab */}
        {selectedTab === 0 && (
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            {/* Left Sidebar - Controls & Employee List */}
            <Box sx={{ 
              width: 320,
              flexShrink: 0, 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2
            }}>
              {/* Department Filter */}
              <DepartmentFilter
                departments={departments}
                selectedDepartments={selectedDepartments}
                onDepartmentChange={setSelectedDepartments}
              />

              {/* Task Stats */}
              <ScheduleStats
                employees={filteredEmployees}
                tasks={tasks}
                currentDate={currentDate}
              />

              {/* Employee List */}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <EmployeeList
                  employees={filteredEmployees}
                  tasks={tasks}
                  onEmployeeClick={canEditPage('schedule') ? (employee) => handleCreateTask(currentDate, employee) : null}
                />
              </Box>
            </Box>

            {/* Main Content - Calendar */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <ScheduleContainer sx={{ width: '100%' }}>
                {/* Calendar Header */}
                <Box sx={{ 
                  p: 3, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 500,
                      color: 'primary.main',
                      fontSize: '1.5rem'
                    }}>
                      {getViewTitle()}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {canEditPage('schedule') && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleCreateTask(currentDate)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            px: 3,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                            }
                          }}
                        >
                          New Task
                        </Button>
                      )}

                      {canEditPage('schedule') && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<SaveIcon />}
                          onClick={() => viewMode === 'day' ? setSaveDayTemplateOpen(true) : setSaveWeekTemplateOpen(true)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1)
                            }
                          }}
                        >
                          Save Layout
                        </Button>
                      )}

                      {canEditPage('schedule') && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LoadIcon />}
                          onClick={handleOpenLoadTemplate}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1)
                            }
                          }}
                        >
                          Load Layout
                        </Button>
                      )}

                      {canDeletePage('schedule') && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ClearIcon />}
                          onClick={handleClearCalendar}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            backgroundColor: alpha(theme.palette.error.main, 0.05),
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              borderColor: theme.palette.error.main
                            }
                          }}
                        >
                          Clear Calendar
                        </Button>
                      )}

                    </Box>
                  </Box>

                  {/* Date Navigation and Time Format */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Top row: View mode, Date picker, Today button, and Time format */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant={viewMode === 'day' ? 'contained' : 'outlined'}
                        size="small"
                        startIcon={<ViewDayIcon />}
                        onClick={() => setViewMode('day')}
                        sx={{ 
                          borderRadius: 2, 
                          fontWeight: 500,
                          backgroundColor: viewMode === 'day' ? undefined : 'white',
                          '&:hover': {
                            backgroundColor: viewMode === 'day' ? undefined : alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        Day
                      </Button>
                      <Button
                        variant={viewMode === 'week' ? 'contained' : 'outlined'}
                        size="small"
                        startIcon={<ViewWeekIcon />}
                        onClick={() => setViewMode('week')}
                        sx={{ 
                          borderRadius: 2, 
                          fontWeight: 500,
                          backgroundColor: viewMode === 'week' ? undefined : 'white',
                          '&:hover': {
                            backgroundColor: viewMode === 'week' ? undefined : alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        Week
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TodayIcon />}
                        onClick={goToToday}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 500,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        Today
                      </Button>

                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dayjs(currentDate)}
                          onChange={(newValue) => {
                            if (newValue) {
                              setCurrentDate(newValue.toDate());
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: {
                                minWidth: 140,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: 'white',
                                  height: 36,
                                  fontSize: '0.8rem',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: '1px',
                                    borderColor: alpha(theme.palette.grey[300], 0.8)
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: '1px',
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: '1px',
                                    borderColor: theme.palette.primary.main
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>

                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel sx={{ fontSize: '0.8rem' }}>Time</InputLabel>
                        <Select
                          value={timeFormat}
                          label="Time"
                          onChange={(e) => updateTimeFormat(e.target.value)}
                          sx={{
                            borderRadius: 2,
                            backgroundColor: 'white',
                            height: 36,
                            fontSize: '0.8rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              borderColor: alpha(theme.palette.grey[300], 0.8)
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              borderColor: theme.palette.primary.main
                            }
                          }}
                        >
                          <MenuItem value="12hour">12 Hour</MenuItem>
                          <MenuItem value="24hour">24 Hour</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Bottom row: Navigation arrows */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        onClick={() => navigateDate('prev')}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      
                      <IconButton 
                        onClick={() => navigateDate('next')}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

                {/* Calendar Content */}
                <ScheduleCalendar
                  currentDate={currentDate}
                  viewMode={viewMode}
                  employees={filteredEmployees}
                  tasks={filteredTasks}
                  onTaskClick={handleViewTask}
                  onTaskCreate={canEditPage('schedule') ? handleCreateTask : null}
                  onTaskDelete={canDeletePage('schedule') ? handleTaskDelete : null}
                  timeFormat={timeFormat}
                  canEdit={canEditPage('schedule')}
                  onLoadTemplate={canEditPage('schedule') ? (targetDate) => {
                    const targetDateStr = format(targetDate, 'yyyy-MM-dd');
                    setLoadStartDate(targetDateStr);
                    setLoadEndDate(targetDateStr);
                    setSelectedTemplateId(null);
                    setLoadDayTemplateOpen(true);
                  } : null}
                />
              </ScheduleContainer>
            </Box>
          </Box>
        )}

        {/* Employee Tasks Tab */}
        {selectedTab === 1 && (
          <Box sx={{ width: '100%', mt: 4 }}>
            {/* Employee Tasks content is now handled by a separate component */}
          </Box>
        )}

        {/* Analytics Tab */}
        {selectedTab === 2 && (
          <Box sx={{ width: '100%', mt: 4 }}>
            {/* Analytics content is now handled by a separate component */}
          </Box>
        )}
      </Box>

      {/* Task View Dialog */}
      <ShiftViewDialog
        open={taskViewOpen}
        onClose={() => setTaskViewOpen(false)}
        task={selectedTask}
        employees={employees}
        departments={departments}
        onEdit={canEditPage('schedule') ? handleEditTask : null}
        onDelete={canDeletePage('schedule') ? handleTaskDelete : null}
        timeFormat={timeFormat}
        canEdit={canEditPage('schedule')}
        canDelete={canDeletePage('schedule')}
      />

      {/* Task Editor Dialog */}
      <ShiftEditor
        open={taskEditorOpen}
        onClose={() => setTaskEditorOpen(false)}
        task={selectedTask}
        employees={employees}
        departments={departments}
        selectedDate={selectedDate}
        selectedEmployee={selectedEmployee}
        onSave={handleTaskSave}
        onDelete={handleTaskDelete}
        timeFormat={timeFormat}
        canEdit={canEditPage('schedule')}
        canDelete={canDeletePage('schedule')}
      />

      {/* Save Day Template Dialog */}
      <Dialog
        open={saveDayTemplateOpen}
        onClose={() => setSaveDayTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Save Day Layout as Template
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Template Name"
              id="day-template-name"
              placeholder="e.g., Monday Schedule, Weekend Coverage"
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              id="day-template-description"
              multiline
              rows={3}
              placeholder="Describe when to use this day layout..."
            />
            <Typography variant="body2" color="text.secondary">
              This will save all tasks for {format(currentDate, 'MMMM d, yyyy')} ({tasks.filter(s => s.shift_date === format(currentDate, 'yyyy-MM-dd')).length} tasks) as a reusable day template.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setSaveDayTemplateOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const name = document.getElementById('day-template-name').value;
              const description = document.getElementById('day-template-description').value;
              if (name.trim()) {
                handleSaveDayTemplate(name, description);
              }
            }}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Week Template Dialog */}
      <Dialog
        open={saveWeekTemplateOpen}
        onClose={() => setSaveWeekTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Save Week Layout as Template
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Template Name"
              id="week-template-name"
              placeholder="e.g., Standard Week, Holiday Week"
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              id="week-template-description"
              multiline
              rows={3}
              placeholder="Describe when to use this week layout..."
            />
            <Typography variant="body2" color="text.secondary">
              This will save all tasks for the current week ({tasks.filter(s => {
                const taskDate = parseISO(s.shift_date);
                const weekStart = startOfWeek(currentDate);
                const weekEnd = endOfWeek(currentDate);
                return taskDate >= weekStart && taskDate <= weekEnd;
              }).length} tasks) as a reusable week template.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setSaveWeekTemplateOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const name = document.getElementById('week-template-name').value;
              const description = document.getElementById('week-template-description').value;
              if (name.trim()) {
                handleSaveWeekTemplate(name, description);
              }
            }}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load Day Template Dialog */}
      <Dialog
        open={loadDayTemplateOpen}
        onClose={() => setLoadDayTemplateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Load Day Template
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Select a template and date range to load tasks.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Date Range
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={loadStartDate ? dayjs(loadStartDate) : null}
                    onChange={(newValue) => {
                      setLoadStartDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    value={loadEndDate ? dayjs(loadEndDate) : null}
                    onChange={(newValue) => {
                      setLoadEndDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Select Template
              </Typography>
              
              {dayTemplates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No day templates available. Save your first day template to get started.
                </Typography>
              ) : (
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {dayTemplates.map((template) => (
                    <ListItemButton
                      key={template.id}
                      selected={selectedTemplateId === template.id}
                      onClick={() => setSelectedTemplateId(template.id)}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        border: `1px solid ${selectedTemplateId === template.id ? theme.palette.primary.main : alpha(theme.palette.grey[300], 0.5)}`,
                        backgroundColor: selectedTemplateId === template.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                    >
                      <ListItemText
                        primary={template.name}
                        secondary={
                          <Box>
                            {template.description && (
                              <Typography variant="body2" color="text.secondary">
                                {template.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {template.template_data?.tasks?.length || 0} tasks • Created {new Date(template.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      {canDeletePage('schedule') && (
                        <IconButton
                          onClick={(e) => handleDeleteTemplate(template, e)}
                          sx={{
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1)
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setLoadDayTemplateOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLoadDayTemplate}
            disabled={!selectedTemplateId || !loadStartDate || !loadEndDate}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Load Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load Week Template Dialog */}
      <Dialog
        open={loadWeekTemplateOpen}
        onClose={() => setLoadWeekTemplateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Load Week Template
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Select a template and date range to load tasks.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Date Range
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={loadStartDate ? dayjs(loadStartDate) : null}
                    onChange={(newValue) => {
                      setLoadStartDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    value={loadEndDate ? dayjs(loadEndDate) : null}
                    onChange={(newValue) => {
                      setLoadEndDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Select Template
              </Typography>
              
              {weekTemplates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No week templates available. Save your first week template to get started.
                </Typography>
              ) : (
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {weekTemplates.map((template) => (
                    <ListItemButton
                      key={template.id}
                      selected={selectedTemplateId === template.id}
                      onClick={() => setSelectedTemplateId(template.id)}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        border: `1px solid ${selectedTemplateId === template.id ? theme.palette.primary.main : alpha(theme.palette.grey[300], 0.5)}`,
                        backgroundColor: selectedTemplateId === template.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                    >
                      <ListItemText
                        primary={template.name}
                        secondary={
                          <Box>
                            {template.description && (
                              <Typography variant="body2" color="text.secondary">
                                {template.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {template.template_data?.tasks?.length || 0} tasks • Created {new Date(template.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      {canDeletePage('schedule') && (
                        <IconButton
                          onClick={(e) => handleDeleteTemplate(template, e)}
                          sx={{
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1)
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setLoadWeekTemplateOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLoadWeekTemplate}
            disabled={!selectedTemplateId || !loadStartDate || !loadEndDate}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Load Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Calendar Dialog */}
      <Dialog
        open={clearCalendarOpen}
        onClose={() => setClearCalendarOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>
          Clear Calendar
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Clear all tasks in the selected date range. This action cannot be undone.
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={clearStartDate ? dayjs(clearStartDate) : null}
                onChange={(newValue) => {
                  setClearStartDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                value={clearEndDate ? dayjs(clearEndDate) : null}
                onChange={(newValue) => {
                  setClearEndDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setClearCalendarOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmClear}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Clear Tasks
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Template Confirmation Dialog */}
      <Dialog
        open={deleteTemplateOpen}
        onClose={() => setDeleteTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>
          Delete Template
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Are you sure you want to delete the template "{templateToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. The template will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setDeleteTemplateOpen(false)} 
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteTemplate}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Delete Template
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Fade>
  );
};

export default Schedule;